import { Badge } from '../../ui/primitives/Badge.tsx';
import { Button } from '../../ui/primitives/Button.tsx';
import { Surface } from '../../ui/primitives/Surface.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import styles from './CompletionStates.module.css';

/**
 * THE EXERCISE THE LEARNER IS STANDING ON CANNOT BE OPENED.
 *
 * The third thing that can happen at bootstrap, and the one the shell used to
 * report as the first: the platform answered, and the answer was that this
 * Exercise cannot be served — a configuration its Exercise Type refuses, an
 * Exercise deleted out from under the cursor, a type this deployment does not
 * carry. The learner saw "Não foi possível carregar sua prática" over a
 * retry button, which named the wrong cause and offered the one action
 * certain to fail again.
 *
 * What makes this state worth its own file rather than a different string on
 * the offline one is that it is NOT A DEAD END, and the offline state cannot
 * say so. The position does not move until an Exercise is answered, so this
 * one Exercise being unanswerable would close the product to the learner
 * outright — unless the screen hands them the two things that still work:
 * what they already did in this Lesson, and the way back to the journey.
 *
 * It says the Exercise cannot be opened and stops there. Which field of which
 * configuration the server refused is an administrator's problem, and putting
 * it in front of a learner would be handing them a fault they can neither read
 * nor fix (§ 12 — a failure states what the reader can do about it).
 */
export function BlockedExercise({
  lessonTitle,
  onRetry,
  retrying = false,
}: {
  lessonTitle: string | null;
  onRetry: () => void;
  /**
   * A retry is out and this screen is still the answer until it comes back.
   * The state does not leave, does not blank, and does not hand the screen to
   * a loading illustration — the control that was pressed carries the whole
   * report, so the only thing that moves is the thing the learner touched.
   */
  retrying?: boolean;
}) {
  return (
    <Surface as="section" layer={2} radius="xl" inset>
      <div className={styles.state} data-testid="blocked-exercise">
        <Badge tone="near-miss" icon="alert-circle">
          Exercício indisponível
        </Badge>

        <Text as="h1" variant="heading">
          Este exercício não pôde ser aberto
        </Text>

        <Text as="p" variant="body">
          {lessonTitle
            ? `O próximo exercício de ${lessonTitle} não está disponível no momento. Já avisamos a equipe.`
            : 'O próximo exercício não está disponível no momento. Já avisamos a equipe.'}
        </Text>

        <Text as="p" variant="body-sm" tone="muted">
          Sua posição e tudo o que você já respondeu continuam guardados.
        </Text>

        {/*
          § 1.6 — one decision, and the way out is not it: the task bar above
          this card already carries "Voltar às lições" on every state of the
          practice screen, so repeating it here would be the same decision
          offered twice. What is left is the retry, a ghost rather than a
          primary, because it is the least likely thing to change anything.
        */}
        <div className={styles.stateActions}>
          {retrying ? (
            <Button
              variant="ghost"
              icon="rotate-ccw"
              disabled
              disabledReason="Estamos tentando abrir o exercício de novo."
            >
              Tentando de novo…
            </Button>
          ) : (
            <Button variant="ghost" icon="rotate-ccw" onClick={onRetry}>
              Tentar de novo
            </Button>
          )}
        </div>
      </div>
    </Surface>
  );
}
